import { useState, useEffect, useCallback } from 'react';
import { useUserStore } from '../stores/userStore';
import { WishlistAPI, SavedProperty } from '../lib/api/wishlists';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

export function useSavedProperties() {
  const { user } = useUserStore();
  const [savedProperties, setSavedProperties] = useState<SavedProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingProperty, setSavingProperty] = useState<string | null>(null);
  const [removingProperty, setRemovingProperty] = useState<string | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);

  // Get Supabase user on mount
  useEffect(() => {
    const getSupabaseUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setSupabaseUser(user);
    };
    getSupabaseUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSupabaseUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch saved properties
  const fetchSavedProperties = useCallback(async () => {
    console.log('🔄 useSavedProperties: fetchSavedProperties called');
    console.log('🔄 useSavedProperties: user ID from store:', user?.id);
    console.log('🔄 useSavedProperties: Supabase user ID:', supabaseUser?.id);
    
    // Prioritize Supabase user ID for RLS compatibility
    const userId = supabaseUser?.id;
    
    if (!userId) {
      console.log('❌ useSavedProperties: No authenticated Supabase user, clearing saved properties');
      setSavedProperties([]);
      setLoading(false);
      return;
    }

    // Verify that the user is actually authenticated with Supabase
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        console.log('❌ useSavedProperties: User not authenticated with Supabase');
        setSavedProperties([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      console.log('🔄 useSavedProperties: Calling WishlistAPI.getSavedProperties...');
      const properties = await WishlistAPI.getSavedProperties(userId);
      console.log('✅ useSavedProperties: Got saved properties:', properties.length);
      setSavedProperties(properties);
    } catch (err) {
      console.error('❌ useSavedProperties: Error fetching saved properties:', err);
      
      // Handle specific error types
      if (err instanceof Error) {
        if (err.message.includes('Auth session missing') || err.message.includes('406')) {
          console.log('🔑 useSavedProperties: Authentication issue detected, clearing saved properties');
          setSavedProperties([]);
          setError('Please sign in to view saved properties');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to fetch saved properties');
      }
    } finally {
      setLoading(false);
    }
  }, [supabaseUser?.id]);

  // Save a property
  const saveProperty = useCallback(async (propertyId: string) => {
    console.log('💾 useSavedProperties: saveProperty called for property:', propertyId);
    console.log('💾 useSavedProperties: user ID from store:', user?.id);
    console.log('💾 useSavedProperties: Supabase user ID:', supabaseUser?.id);
    
    if (!propertyId || typeof propertyId !== 'string') {
      console.log('❌ useSavedProperties: Invalid property ID provided');
      return false;
    }
    
    // Only use Supabase user ID for RLS compatibility
    const userId = supabaseUser?.id;
    
    if (!userId) {
      console.log('❌ useSavedProperties: No authenticated Supabase user, cannot save property');
      setError('Please sign in to save properties');
      return false;
    }

    // Verify authentication before proceeding
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        console.log('❌ useSavedProperties: User not authenticated with Supabase');
        setError('Please sign in to save properties');
        return false;
      }
    } catch (authError) {
      console.error('❌ useSavedProperties: Authentication check failed:', authError);
      setError('Authentication required to save properties');
      return false;
    }

    try {
      setSavingProperty(propertyId);
      setError(null);
      console.log('🔄 useSavedProperties: Calling WishlistAPI.saveProperty...');
      await WishlistAPI.saveProperty(userId, propertyId);
      console.log('✅ useSavedProperties: Property saved successfully');
      
      // Refresh the list
      await fetchSavedProperties();
      return true;
    } catch (err) {
      console.error('❌ useSavedProperties: Error saving property:', err);
      setError(err instanceof Error ? err.message : 'Failed to save property');
      return false;
    } finally {
      setSavingProperty(null);
    }
  }, [supabaseUser?.id, fetchSavedProperties]);

  // Remove a property
  const removeProperty = useCallback(async (propertyId: string) => {
    console.log('🗑️ useSavedProperties: removeProperty called for property:', propertyId);
    console.log('🗑️ useSavedProperties: user ID from store:', user?.id);
    console.log('🗑️ useSavedProperties: Supabase user ID:', supabaseUser?.id);
    
    if (!propertyId || typeof propertyId !== 'string') {
      console.log('❌ useSavedProperties: Invalid property ID provided');
      return false;
    }
    
    // Only use Supabase user ID for RLS compatibility
    const userId = supabaseUser?.id;
    
    if (!userId) {
      console.log('❌ useSavedProperties: No authenticated Supabase user, cannot remove property');
      setError('Please sign in to manage saved properties');
      return false;
    }

    // Verify authentication before proceeding
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        console.log('❌ useSavedProperties: User not authenticated with Supabase');
        setError('Please sign in to manage saved properties');
        return false;
      }
    } catch (authError) {
      console.error('❌ useSavedProperties: Authentication check failed:', authError);
      setError('Authentication required to manage saved properties');
      return false;
    }

    try {
      setRemovingProperty(propertyId);
      setError(null);
      console.log('🔄 useSavedProperties: Calling WishlistAPI.removeProperty...');
      await WishlistAPI.removeProperty(userId, propertyId);
      console.log('✅ useSavedProperties: Property removed successfully');
      
      // Remove from local state
      setSavedProperties(prev => prev.filter(prop => prop.id !== propertyId));
      return true;
    } catch (err) {
      console.error('❌ useSavedProperties: Error removing property:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove property');
      return false;
    } finally {
      setRemovingProperty(null);
    }
  }, [supabaseUser?.id]);

  // Check if a property is saved
  const isPropertySaved = useCallback((propertyId: string): boolean => {
    if (!propertyId || typeof propertyId !== 'string') return false;
    return savedProperties.some(prop => prop.id === propertyId);
  }, [savedProperties]);

  // Toggle save/unsave a property
  const togglePropertySaved = useCallback(async (propertyId: string) => {
    if (!propertyId || typeof propertyId !== 'string') {
      console.log('❌ useSavedProperties: Invalid property ID provided to togglePropertySaved');
      return false;
    }
    
    if (isPropertySaved(propertyId)) {
      return await removeProperty(propertyId);
    } else {
      return await saveProperty(propertyId);
    }
  }, [isPropertySaved, removeProperty, saveProperty]);

  // Get saved properties count
  const getSavedCount = useCallback(async (): Promise<number> => {
    const userId = supabaseUser?.id;
    if (!userId) return 0;

    // Verify authentication
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return 0;
      
      return await WishlistAPI.getSavedPropertiesCount(userId);
    } catch (err) {
      console.error('Error getting saved count:', err);
      return savedProperties.length; // Fallback to local state
    }
  }, [supabaseUser?.id, savedProperties.length]);

  // Filter saved properties
  const filterSavedProperties = useCallback((
    searchQuery: string = '',
    propertyType: string = 'all',
    sortBy: string = 'date'
  ) => {
    let filtered = [...savedProperties];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(prop => {
        // Add safety checks for required fields
        if (!prop || !prop.title || !prop.description || !prop.propertyType) return false;
        
        return prop.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
               prop.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
               prop.propertyType.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    // Apply property type filter
    if (propertyType !== 'all') {
      filtered = filtered.filter(prop => {
        // Add safety check for propertyType
        if (!prop || !prop.propertyType) return false;
        return prop.propertyType === propertyType;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price_asc':
        filtered.sort((a, b) => {
          const priceA = typeof a.price === 'number' ? a.price : 0;
          const priceB = typeof b.price === 'number' ? b.price : 0;
          return priceA - priceB;
        });
        break;
      case 'price_desc':
        filtered.sort((a, b) => {
          const priceA = typeof a.price === 'number' ? a.price : 0;
          const priceB = typeof b.price === 'number' ? b.price : 0;
          return priceB - priceA;
        });
        break;
      case 'name':
        filtered.sort((a, b) => {
          const titleA = a.title || '';
          const titleB = b.title || '';
          return titleA.localeCompare(titleB);
        });
        break;
      case 'date':
      default:
        filtered.sort((a, b) => {
          const dateA = a.saved_at ? new Date(a.saved_at).getTime() : 0;
          const dateB = b.saved_at ? new Date(b.saved_at).getTime() : 0;
          return dateB - dateA;
        });
        break;
    }

    return filtered;
  }, [savedProperties]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch saved properties when authenticated Supabase user is available
  useEffect(() => {
    // Only fetch if we have an authenticated Supabase user
    if (supabaseUser?.id) {
      fetchSavedProperties();
    } else {
      // Clear saved properties if no authenticated user
      setSavedProperties([]);
      setLoading(false);
    }
  }, [fetchSavedProperties, supabaseUser?.id]);

  return {
    savedProperties,
    loading,
    error,
    savingProperty,
    removingProperty,
    fetchSavedProperties,
    saveProperty,
    removeProperty,
    isPropertySaved,
    togglePropertySaved,
    getSavedCount,
    filterSavedProperties,
    clearError
  };
}
